'use client';

import {Component, ErrorInfo, ReactNode} from 'react';
import {Button} from '@/shadcn/components/ui/button';
import {AlertTriangle, RefreshCw} from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error: Error): State {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Call the optional onError callback
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }

        // Report error to enhanced monitoring service
        if (typeof window !== 'undefined') {
            import('@/lib/error-reporter').then(({errorReporter}) => {
                errorReporter.reportError(error, {
                    type: 'REACT_ERROR_BOUNDARY',
                    componentStack: errorInfo.componentStack,
                    context: 'Error caught by React Error Boundary'
                });
            }).catch(reportingError => {
                console.error('Failed to report boundary error:', reportingError);
            });
        }
    }

    handleReset = () => {
        this.setState({hasError: false, error: undefined});
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI or use provided fallback
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex flex-col items-center justify-center p-8 text-center space-y-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
                        <AlertTriangle className="w-8 h-8 text-red-600"/>
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Etwas ist schief gelaufen
                        </h2>
                        <p className="text-slate-600 max-w-md">
                            Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.
                        </p>
                    </div>
                    <Button
                        onClick={this.handleReset}
                        variant="primary"
                    >
                        <RefreshCw className="w-4 h-4 mr-2"/>
                        Erneut versuchen
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;

// Functional hook-based error boundary for specific use cases
export function withErrorBoundary<T extends object>(
    Component: React.ComponentType<T>,
    fallback?: ReactNode,
    onError?: (error: Error, errorInfo: ErrorInfo) => void
) {
    const WrappedComponent = (props: T) => (
        <ErrorBoundary fallback={fallback} onError={onError}>
            <Component {...props} />
        </ErrorBoundary>
    );

    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
    return WrappedComponent;
}

// Specific error boundary for async operations
export class AsyncErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error: Error): State {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('AsyncErrorBoundary caught an error:', error, errorInfo);

        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }

        // Report async errors with enhanced monitoring
        if (typeof window !== 'undefined') {
            import('@/lib/error-reporter').then(({errorReporter}) => {
                errorReporter.reportError(error, {
                    type: 'ASYNC_ERROR_BOUNDARY',
                    componentStack: errorInfo.componentStack,
                    context: 'Async operation failure - caught by AsyncErrorBoundary'
                });
            }).catch(reportingError => {
                console.error('Failed to report async boundary error:', reportingError);
            });
        }
    }

    handleReset = () => {
        this.setState({hasError: false, error: undefined});
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center p-4 text-center space-y-3 bg-gray-50 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-amber-600"/>
                    <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-900">Fehler beim Laden</p>
                        <p className="text-xs text-slate-600">
                            Daten konnten nicht geladen werden
                        </p>
                    </div>
                    <Button
                        onClick={this.handleReset}
                        size="sm"
                        variant="outline"
                        className="text-xs"
                    >
                        <RefreshCw className="w-3 h-3 mr-1"/>
                        Erneut laden
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}
